import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Users, Copy, Crown, UserPlus, PlusCircle, LogOut, Pencil, X, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Spinner } from "@/components/shared";

const CHECKINS = ["I'm good", "At the meeting point", "Heading home"];
const DURATIONS = [
  { id: "30m", label: "30 minutes" },
  { id: "1h", label: "1 hour" },
  { id: "road_day", label: "Until Road Day ends" },
];

export default function Crew() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [share, setShare] = useState({ active: false, until: null });

  // no-crew form state
  const [mode, setMode] = useState(null); // 'create' | 'join'
  const [crewName, setCrewName] = useState("");
  const [meetPoint, setMeetPoint] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  // lead edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editMeet, setEditMeet] = useState("");

  const load = () => api.get("/me/crew").then(({ data }) => {
    setData(data);
    const me = (data.members || []).find((m) => m.is_me);
    if (me) setShare({ active: me.location_sharing, until: me.location_until });
    if (data.crew) { setEditName(data.crew.name); setEditMeet(data.crew.meeting_point || ""); }
  });
  useEffect(() => { load(); }, []);

  const checkin = async (status) => {
    await api.post("/crew/checkin", { status });
    toast.success(`Checked in: "${status}"`);
    load();
  };
  const shareLoc = async (duration) => {
    const { data } = await api.post("/crew/location", { duration });
    setShare({ active: data.active, until: data.until });
    toast.success(duration === "stop" ? "Location sharing stopped" : "Location sharing active");
    load();
  };
  const fmtUntil = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

  const createCrew = async () => {
    setBusy(true);
    try {
      await api.post("/crew/create", { name: crewName, meeting_point: meetPoint });
      toast.success("Crew created — you're the Crew Lead!");
      setMode(null); setCrewName(""); setMeetPoint("");
      await refresh(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const joinCrew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/crew/join", { code: joinCode.trim() });
      toast.success(`Joined ${data.name}`);
      setMode(null); setJoinCode("");
      await refresh(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const leaveCrew = async () => {
    if (!window.confirm("Leave this crew? You can rejoin with the code.")) return;
    await api.post("/crew/leave");
    toast.success("You left the crew");
    await refresh(); load();
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the crew?`)) return;
    await api.post(`/crew/remove/${m.id}`);
    toast.success(`${m.name} removed`);
    load();
  };
  const transferLead = async (m) => {
    if (!window.confirm(`Make ${m.name} the new Crew Lead? You'll become a regular member.`)) return;
    await api.post(`/crew/transfer/${m.id}`);
    toast.success(`${m.name} is now Crew Lead`);
    load();
  };
  const saveEdit = async () => {
    await api.put("/crew", { name: editName, meeting_point: editMeet });
    toast.success("Crew details updated");
    setEditing(false); load();
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.crew.code);
      toast.success("Crew code copied — share it with your people");
    } catch { toast.error("Couldn't copy — long-press the code to copy it"); }
  };
  const shareCode = async () => {
    const text = `Join my Carnival crew "${data.crew.name}" — code: ${data.crew.code}`;
    try {
      if (navigator.share) await navigator.share({ title: "Join my crew", text });
      else { navigator.clipboard.writeText(text); toast.success("Invite copied"); }
    } catch { /* cancelled */ }
  };

  if (!data) return <div className="px-6 pt-10"><Spinner /></div>;

  return (
    <Page className="px-5 pt-6">
      <button data-testid="crew-back" onClick={() => nav(-1)} className="cs-tap flex items-center gap-1 text-sm mb-4" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Back
      </button>
      <h1 className="font-head font-extrabold text-3xl">My Crew</h1>

      {!data.crew ? (
        /* ---------- NO CREW: create or join ---------- */
        <>
          <p className="mt-1 text-base" style={{ color: "var(--cs-fg2)" }}>Create a crew and invite your people with a code, or join one you were given.</p>

          {mode === null && (
            <div className="mt-6 space-y-3">
              <button data-testid="crew-create-open" onClick={() => setMode("create")}
                className="cs-tap w-full magenta-grad magenta-glow rounded-2xl p-5 flex items-center gap-3 text-white text-left">
                <PlusCircle size={26} />
                <div><p className="font-head font-bold text-lg leading-none">Create a Crew</p><p className="text-xs mt-1 opacity-90">You'll be the Crew Lead</p></div>
              </button>
              <button data-testid="crew-join-open" onClick={() => setMode("join")}
                className="cs-card cs-tap w-full p-5 flex items-center gap-3 text-left">
                <UserPlus size={26} style={{ color: "var(--cs-magenta)" }} />
                <div><p className="font-head font-bold text-lg leading-none">Join with a Code</p><p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>Enter the code from your Crew Lead</p></div>
              </button>
            </div>
          )}

          {mode === "create" && (
            <div className="cs-card p-4 mt-6 rise">
              <p className="text-xs font-bold tracking-wide mb-2" style={{ color: "var(--cs-fg2)" }}>NEW CREW</p>
              <input data-testid="crew-create-name" value={crewName} onChange={(e) => setCrewName(e.target.value)} placeholder="Crew name (e.g. Sunrise Crew)" className="crew-input" />
              <input data-testid="crew-create-meet" value={meetPoint} onChange={(e) => setMeetPoint(e.target.value)} placeholder="Meeting point (optional)" className="crew-input mt-2" />
              <div className="flex gap-2 mt-3">
                <button data-testid="crew-create-submit" onClick={createCrew} disabled={busy || !crewName.trim()} className="cs-tap flex-1 magenta-grad rounded-xl py-3 font-bold text-white text-sm">{busy ? "Creating…" : "Create Crew"}</button>
                <button onClick={() => setMode(null)} className="cs-tap rounded-xl px-4" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}><X size={18} /></button>
              </div>
            </div>
          )}

          {mode === "join" && (
            <div className="cs-card p-4 mt-6 rise">
              <p className="text-xs font-bold tracking-wide mb-2" style={{ color: "var(--cs-fg2)" }}>CREW CODE</p>
              <input data-testid="crew-join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. SUN42" className="crew-input font-mono tracking-widest text-lg text-center" />
              <div className="flex gap-2 mt-3">
                <button data-testid="crew-join-submit" onClick={joinCrew} disabled={busy || !joinCode.trim()} className="cs-tap flex-1 magenta-grad rounded-xl py-3 font-bold text-white text-sm">{busy ? "Joining…" : "Join Crew"}</button>
                <button onClick={() => setMode(null)} className="cs-tap rounded-xl px-4" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}><X size={18} /></button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ---------- HAS CREW ---------- */
        <>
          <p className="mt-1 text-sm" style={{ color: "var(--cs-fg2)" }}>{data.crew.name}{data.me_is_lead && " · You're the Lead"}</p>

          {/* invite / code card */}
          <div className="cs-card mt-5 p-4" style={{ borderColor: "var(--cs-magenta)" }}>
            <p className="text-xs font-bold tracking-wide" style={{ color: "var(--cs-fg2)" }}>CREW CODE — SHARE TO INVITE</p>
            <div className="flex items-center justify-between mt-2">
              <span data-testid="crew-code-value" className="font-mono font-extrabold text-2xl tracking-widest" style={{ color: "var(--cs-magenta)" }}>{data.crew.code}</span>
              <div className="flex gap-2">
                <button data-testid="crew-copy-code" onClick={copyCode} className="cs-tap rounded-xl p-2.5" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}><Copy size={18} /></button>
                <button data-testid="crew-share-code" onClick={shareCode} className="cs-tap rounded-xl p-2.5 magenta-grad text-white"><Share2 size={18} /></button>
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--cs-fg2)" }}>Anyone in your band can join with this code. Only the Lead can remove people.</p>
          </div>

          {/* meeting point + lead edit */}
          <div className="cs-card mt-4 p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold tracking-wide" style={{ color: "var(--cs-fg2)" }}>MEETING POINT</p>
              {data.me_is_lead && !editing && (
                <button data-testid="crew-edit-open" onClick={() => setEditing(true)} className="cs-tap text-xs font-semibold flex items-center gap-1" style={{ color: "var(--cs-magenta)" }}><Pencil size={13} /> Edit</button>
              )}
            </div>
            {editing ? (
              <div className="mt-2">
                <input data-testid="crew-edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Crew name" className="crew-input" />
                <input data-testid="crew-edit-meet" value={editMeet} onChange={(e) => setEditMeet(e.target.value)} placeholder="Meeting point" className="crew-input mt-2" />
                <div className="flex gap-2 mt-2">
                  <button data-testid="crew-edit-save" onClick={saveEdit} className="cs-tap flex-1 magenta-grad rounded-xl py-2.5 font-bold text-white text-sm">Save</button>
                  <button onClick={() => setEditing(false)} className="cs-tap rounded-xl px-4" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}><X size={18} /></button>
                </div>
              </div>
            ) : (
              <p className="mt-1 font-semibold flex items-center gap-2"><MapPin size={16} style={{ color: "var(--cs-green)" }} /> {data.crew.meeting_point || "Not set yet"}</p>
            )}
          </div>

          {/* members */}
          <div className="cs-card mt-4 p-4">
            <p className="text-xs font-bold tracking-wide mb-3" style={{ color: "var(--cs-fg2)" }}>CREW MEMBERS ({data.members.length})</p>
            <div className="space-y-3">
              {data.members.map((m) => (
                <div key={m.id} data-testid={`crew-member-${m.id}`} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center rounded-full font-bold text-sm" style={{ width: 38, height: 38, background: "var(--cs-card-hi)", color: "var(--cs-magenta)" }}>
                      {m.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </span>
                    <div>
                      <p className="font-semibold text-sm flex items-center gap-1.5">
                        {m.name}{m.is_me && " (You)"}
                        {m.is_lead && <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: "rgba(224,26,79,0.15)", color: "var(--cs-magenta)" }}><Crown size={10} /> LEAD</span>}
                      </p>
                      <p className="text-xs flex items-center gap-1" style={{ color: m.checkin_status ? "var(--cs-green)" : "var(--cs-fg2)" }}>
                        {m.location_sharing && <MapPin size={12} style={{ color: "var(--cs-green)" }} />}
                        {m.checkin_status || "No current status"}
                      </p>
                    </div>
                  </div>
                  {data.me_is_lead && !m.is_me && (
                    <div className="flex gap-1.5">
                      <button data-testid={`crew-transfer-${m.id}`} onClick={() => transferLead(m)} title="Make Lead" className="cs-tap rounded-lg p-2" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}><Crown size={14} style={{ color: "var(--cs-amber)" }} /></button>
                      <button data-testid={`crew-remove-${m.id}`} onClick={() => removeMember(m)} title="Remove" className="cs-tap rounded-lg p-2" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}><X size={14} style={{ color: "var(--cs-red)" }} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* quick check-in */}
          <div className="cs-card mt-4 p-4">
            <p className="text-xs font-bold tracking-wide mb-3" style={{ color: "var(--cs-fg2)" }}>QUICK CHECK-IN</p>
            <div className="grid grid-cols-1 gap-2">
              {CHECKINS.map((c) => (
                <button key={c} data-testid={`crew-checkin-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => checkin(c)}
                  className="cs-tap rounded-xl py-3 font-semibold text-sm text-left px-4" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}>{c}</button>
              ))}
            </div>
          </div>

          {/* location sharing */}
          <div className="cs-card mt-4 p-4" style={{ borderColor: share.active ? "var(--cs-green)" : "var(--cs-border)" }}>
            <p className="text-xs font-bold tracking-wide mb-1" style={{ color: "var(--cs-fg2)" }}>LOCATION SHARING</p>
            {share.active ? (
              <>
                <p className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--cs-green)" }}><Clock size={15} /> Active until {fmtUntil(share.until)}</p>
                <button data-testid="crew-stop-sharing" onClick={() => shareLoc("stop")} className="cs-tap w-full mt-3 rounded-xl py-3 font-bold text-sm" style={{ background: "var(--cs-red)", color: "#fff" }}>Stop sharing now</button>
              </>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: "var(--cs-fg2)" }}>Sharing is always temporary and you can stop anytime.</p>
                <div className="space-y-2">
                  {DURATIONS.map((d) => (
                    <button key={d.id} data-testid={`crew-share-${d.id}`} onClick={() => shareLoc(d.id)}
                      className="cs-tap w-full rounded-xl py-3 font-semibold text-sm flex items-center justify-between px-4" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}>
                      Share for {d.label} <MapPin size={16} style={{ color: "var(--cs-green)" }} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button data-testid="crew-leave" onClick={leaveCrew} className="cs-tap w-full mt-4 mb-2 rounded-2xl flex items-center justify-center gap-2 font-semibold" style={{ height: 50, background: "var(--cs-card)", border: "1px solid var(--cs-border)", color: "var(--cs-red)" }}>
            <LogOut size={17} /> Leave crew
          </button>
        </>
      )}

      <style>{`.crew-input{width:100%;height:48px;border-radius:.7rem;padding:0 1rem;background:var(--cs-card-hi);border:1px solid var(--cs-border);color:#fff;outline:none;font-size:16px}.crew-input:focus{border-color:var(--cs-magenta)}`}</style>
    </Page>
  );
}
